// Private to @github-ui/sanitize. NOT exported from package.json — the
// `github-sanitize` Trusted Types policy must only be reachable through
// sanitize() / sanitizeWithPurifier(), so that anything assigned to a
// TrustedHTML sink in CSP-`trusted-types`-enforced contexts has actually
// passed through DOMPurify.

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendCustomMetric} from '@github-ui/stats'
import type {TrustedHTML, TrustedTypePolicy, TrustedTypePolicyFactory} from 'trusted-types/lib/index'

const POLICY_NAME = 'github-sanitize'
const POLICY_TAGS = {trusted_types_policy_name: POLICY_NAME} as const

type SanitizePolicy = Pick<TrustedTypePolicy, 'name' | 'createHTML' | 'createScriptURL'>

let cachedPolicy: SanitizePolicy | null | undefined

// Test seam — the real `window.trustedTypes` in browser test runners is a
// non-configurable accessor that cannot be replaced via vi.stubGlobal.
let trustedTypesOverride: TrustedTypePolicyFactory | null | undefined

function resolveTrustedTypes(): TrustedTypePolicyFactory | undefined {
  if (trustedTypesOverride !== undefined) {
    return trustedTypesOverride ?? undefined
  }
  if (typeof window === 'undefined') return undefined
  return (window as unknown as {trustedTypes?: TrustedTypePolicyFactory}).trustedTypes
}

/**
 * Lazily create and memoize the `github-sanitize` Trusted Types policy.
 * Returns null when Trusted Types are unavailable, the feature flag is
 * off, or the policy could not be created.
 */
export function getSanitizePolicy(): SanitizePolicy | null {
  if (cachedPolicy !== undefined) {
    return cachedPolicy
  }

  const tt = resolveTrustedTypes()
  if (!isFeatureEnabled('trusted_types_sanitize_policy') || tt === undefined) {
    cachedPolicy = null
    return cachedPolicy
  }

  try {
    cachedPolicy = tt.createPolicy(POLICY_NAME, {
      createHTML: input => input,
      // DOMPurify requires `createScriptURL` on any policy it consumes via
      // its `TRUSTED_TYPES_POLICY` config; identity is safe because DOMPurify
      // only forwards already-validated URLs to this hook.
      createScriptURL: input => input,
    })
  } catch (err) {
    cachedPolicy = null
    // Duplicate policy creation is the common-and-expected failure mode
    // (multiple bundles, HMR, etc.) — track it separately from real errors.
    const isDuplicate =
      err instanceof TypeError && typeof err.message === 'string' && err.message.toLowerCase().includes('policy')
    sendCustomMetric({
      name: isDuplicate ? 'TRUSTED_TYPES_POLICY_INITIALIZED_TWICE' : 'TRUSTED_TYPES_POLICY_ERROR',
      value: 1,
      tags: POLICY_TAGS,
    })
  }

  return cachedPolicy ?? null
}

/**
 * Wrap an already-sanitized HTML string as a TrustedHTML when the
 * `github-sanitize` policy is active. Falls back to returning the input
 * string unchanged when Trusted Types are unavailable or the policy
 * could not be created.
 */
export function wrapAsTrustedHTML(html: string): string | TrustedHTML {
  const policy = getSanitizePolicy()
  if (policy === null) {
    return html
  }

  try {
    const trusted = policy.createHTML(html)
    sendCustomMetric({name: 'TRUSTED_TYPES_POLICY_CALLED', value: 1, tags: POLICY_TAGS})
    return trusted
  } catch {
    sendCustomMetric({name: 'TRUSTED_TYPES_POLICY_ERROR', value: 1, tags: POLICY_TAGS})
    return html
  }
}

/**
 * Reset the memoized policy. Test-only — not part of the public package API.
 */
export function _resetSanitizePolicyForTests(): void {
  cachedPolicy = undefined
}

/**
 * Inject a fake `TrustedTypePolicyFactory` (or `null` to simulate the
 * platform global being absent). Test-only — pass `undefined` to revert
 * to the real platform global.
 */
export function _setTrustedTypesForTests(impl: TrustedTypePolicyFactory | null | undefined): void {
  trustedTypesOverride = impl
}
